// (c) chaehongee. Confidential and proprietary.

import { http, buildApiUrl } from './http'
import { formatCurrency } from '../i18n/formatters'

export type CreditPurchaseItem = {
    id: string
    purchased_at: string
    dollars: number
    credits_added: number
    status: string
    provider: string
    currency: string
    amount: number | null
}

export type CreditPurchaseHistoryResponse = {
    purchases: CreditPurchaseItem[]
}

export type MockCreditPurchaseResponse = {
    credit_balance: number
    purchase: CreditPurchaseItem
}

export type CheckoutSessionResponse = {
    purchase: CreditPurchaseItem
    checkout_url: string
}

export type CreditPreset = { amount: number; credits: number }
export type BillingConfig = { currency: string; symbol: string; presets: CreditPreset[] }

export async function getBillingConfig(): Promise<BillingConfig> {
    return http<BillingConfig>(buildApiUrl('/billing/config'), { method: 'GET' })
}

export function formatCurrencyAmount(currency: string, amount: number): string {
    return formatCurrency(amount, currency.toUpperCase())
}

export async function listCreditPurchases(limit = 50): Promise<CreditPurchaseHistoryResponse> {
    const params = new URLSearchParams({ limit: String(limit) })
    return http<CreditPurchaseHistoryResponse>(buildApiUrl(`/billing/purchases?${params.toString()}`), {
        method: 'GET',
    })
}

export async function mockPurchaseCredits(dollars: number): Promise<MockCreditPurchaseResponse> {
    return http<MockCreditPurchaseResponse>(buildApiUrl('/billing/mock/purchase'), {
        method: 'POST',
        body: JSON.stringify({ dollars }),
    })
}

export async function createCheckoutSession(params: {
    currency: string
    amount: number
}): Promise<CheckoutSessionResponse> {
    return http<CheckoutSessionResponse>(buildApiUrl('/billing/checkout-session'), {
        method: 'POST',
        body: JSON.stringify(params),
    })
}

export async function cancelCreditPurchase(purchaseId: string): Promise<CreditPurchaseItem> {
    return http<CreditPurchaseItem>(buildApiUrl(`/billing/purchases/${purchaseId}/cancel`), {
        method: 'POST',
    })
}

// ── Subscription types & API ─────────────────────────────

export type SubscriptionTierInfo = {
    tier: string
    monthly_credits: number
    /** Monthly price in the smallest currency unit (cents for USD, won for KRW). */
    price_monthly: number
    /** Annual price in the smallest currency unit (cents for USD, won for KRW). */
    price_annual: number
    currency: string
    topup_bonus_pct: number
    max_rollover_credits: number
}

export type AvailableTiersResponse = {
    tiers: SubscriptionTierInfo[]
    current_tier: string | null
}

export type SubscriptionCheckoutResponse = {
    checkout_url: string
    session_id: string
}

export type SubscriptionStatusResponse = {
    tier: string
    status: string
    interval: string
    currency: string
    current_period_start: string | null
    current_period_end: string | null
    cancel_at_period_end: boolean
    monthly_credit_allocation: number
}

export async function getSubscriptionTiers(): Promise<AvailableTiersResponse> {
    return http<AvailableTiersResponse>(buildApiUrl('/billing/subscription/tiers'), {
        method: 'GET',
    })
}

export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    return http<SubscriptionStatusResponse>(buildApiUrl('/billing/subscription'), {
        method: 'GET',
    })
}

export async function createSubscriptionCheckout(
    tier: string,
    interval: 'monthly' | 'annual',
): Promise<SubscriptionCheckoutResponse> {
    // Currency is resolved server-side from the deploy region.
    return http<SubscriptionCheckoutResponse>(buildApiUrl('/billing/subscription/checkout'), {
        method: 'POST',
        body: JSON.stringify({ tier, interval }),
    })
}

export async function cancelSubscription(
    immediate: boolean = false,
): Promise<SubscriptionStatusResponse> {
    return http<SubscriptionStatusResponse>(buildApiUrl('/billing/subscription/cancel'), {
        method: 'POST',
        body: JSON.stringify({ immediate }),
    })
}

export async function changeSubscriptionTier(
    tier: string,
    interval?: string,
): Promise<SubscriptionStatusResponse> {
    return http<SubscriptionStatusResponse>(buildApiUrl('/billing/subscription/change'), {
        method: 'POST',
        body: JSON.stringify({ tier, ...(interval ? { interval } : {}) }),
    })
}
